'use client';

import React, { createContext, useContext, useCallback, useState } from 'react';
import { CitationMap } from '@/lib/utils/citations';
import { CITATIONS_CONFIG } from '@/lib/config/citations.config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CitationContextValue {
  citationMap: CitationMap;
  adviceId: string;
  /**
   * The tab key most recently requested by a citation badge click.
   * SourcesInChatResponse syncs its own activeTab state from this value.
   * Bumps `requestSeq` on every change so useEffect can detect it.
   */
  requestedTab: string | null;
  /** Monotonically increasing counter — lets useEffect detect repeat requests for the same tab */
  requestSeq: number;
  /** Scroll to and highlight a source card by its citation number */
  scrollToSource: (citationNumber: number) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CitationContext = createContext<CitationContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CitationProviderProps {
  citationMap: CitationMap;
  adviceId: string;
  children: React.ReactNode;
}

export const CitationProvider: React.FC<CitationProviderProps> = ({
  citationMap,
  adviceId,
  children,
}) => {
  const [requestedTab, setRequestedTab] = useState<string | null>(null);
  const [requestSeq, setRequestSeq] = useState(0);
  // Store the pending citation number so scroll happens after tab switch
  const [pendingScroll, setPendingScroll] = useState<number | null>(null);

  const scrollToSource = useCallback(
    (citationNumber: number) => {
      const source = citationMap[citationNumber];
      if (!source) return;

      // Request the tab switch — SourcesInChatResponse will pick this up
      const tabKey = source.tabKey;
      if (tabKey) {
        setRequestedTab(tabKey);
        setRequestSeq((prev) => prev + 1);
      }

      // Schedule the scroll after tab switch renders
      const {
        SCROLL: { TAB_SWITCH_DELAY_MS, BEHAVIOR, BLOCK, HIGHLIGHT_DURATION_MS },
        SOURCE_DOM_ID_PREFIX,
      } = CITATIONS_CONFIG;

      setTimeout(() => {
        const elementId = `${SOURCE_DOM_ID_PREFIX}${adviceId}-${citationNumber}`;
        const el = document.getElementById(elementId);
        if (!el) return;

        el.scrollIntoView({
          behavior: BEHAVIOR,
          block: BLOCK,
        });

        // Add highlight class, remove after duration
        el.classList.add('citation-highlight');
        setTimeout(() => {
          el.classList.remove('citation-highlight');
        }, HIGHLIGHT_DURATION_MS);
      }, TAB_SWITCH_DELAY_MS);
    },
    [citationMap, adviceId]
  );

  const value: CitationContextValue = {
    citationMap,
    adviceId,
    requestedTab,
    requestSeq,
    scrollToSource,
  };

  return (
    <CitationContext.Provider value={value}>
      {children}
    </CitationContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the citation context. Returns null if used outside CitationProvider
 * (safe for components that render both inside and outside the provider).
 */
export function useCitationContext(): CitationContextValue | null {
  return useContext(CitationContext);
}
